import { Pipe, PipeTransform } from '@angular/core';
import { LocalizationService } from '../../services/localization/localization.service';

type RoundMode = 'round' | 'trunc' | 'floor' | 'ceil';

@Pipe({
  name: 'currencyCompact',
  pure: false
})
export class CurrencyCompactPipe implements PipeTransform {
  constructor(private loc: LocalizationService) {}

  /**
   * Formatea moneda de forma compacta (k, M, B, T) por encima de un umbral.
   * @param value             número a formatear
   * @param decimalLength     decimales en versión compacta (default 1)
   * @param threshold         umbral para abreviar (default 1000)
   * @param noDecimalIfZero   quita decimales si terminan en 0 (default true)
   * @param roundMode         'round' (por defecto), 'trunc', 'floor' o 'ceil'
   */
  transform(
    value: any,
    decimalLength: number = 1,
    threshold: number = 1000,
    noDecimalIfZero: boolean = true,
    roundMode: RoundMode = 'round'
  ): string {
    if (value == null || value === '') return '';
    const n = Number(value);
    if (!isFinite(n)) return '';

    const symbol  = this.loc?.localeData?.currency?.symbol ?? '$';
    const decSep  = this.loc?.localeData?.currency?.decimalSeparator ?? ',';
    const thouSep = this.loc?.localeData?.currency?.thousandSeparator ?? '.';

    // Debajo del umbral -> formato normal con miles y 2 decimales
    if (Math.abs(n) < threshold) {
      return `${symbol} ${this.formatNormal(n, 2, thouSep, decSep)}`;
    }

    // Encima/igual del umbral -> abreviado
    const suffixes = ['', 'k', 'M', 'B', 'T'];
    const tier = Math.min(4, Math.floor(Math.log10(Math.abs(n)) / 3)); // 1→k, 2→M, 3→B, 4→T
    const scale = Math.pow(10, tier * 3);
    const scaled = n / scale;

    const adjusted = this.applyRounding(scaled, decimalLength, roundMode);
    let fixed = adjusted.toFixed(Math.max(0, decimalLength)).replace('.', decSep);

    if (noDecimalIfZero && decimalLength > 0) {
      const zeroDec = decSep + '0'.repeat(decimalLength);
      if (fixed.endsWith(zeroDec)) fixed = fixed.slice(0, -zeroDec.length);
    }

    return `${symbol} ${fixed}${suffixes[tier]}`;
  }

  private formatNormal(
    n: number,
    decimalLength: number,
    thouSep: string,
    decSep: string
  ): string {
    const parts = Number(n).toFixed(decimalLength).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thouSep);
    return parts.join(decSep);
  }

  private applyRounding(value: number, decimals: number, mode: RoundMode): number {
    const factor = Math.pow(10, Math.max(0, decimals));
    switch (mode) {
      case 'trunc':
        // Trunca hacia 0 (2.599 -> 2.59, -2.599 -> -2.59)
        return Math.trunc(value * factor) / factor;
      case 'floor':
        // Siempre hacia -∞ (2.599 -> 2.59, -2.591 -> -2.60)
        return Math.floor(value * factor) / factor;
      case 'ceil':
        // Siempre hacia +∞ (2.591 -> 2.60, -2.599 -> -2.59)
        return Math.ceil(value * factor) / factor;
      case 'round':
      default:
        return Math.round(value * factor) / factor;
    }
  }
}
